import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, ScaleIcon } from '@heroicons/react/24/outline';
import { academyAPI } from '../../utils/api';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';
import TrialTranscript from '../../components/student/TrialTranscript';

const quotaMessage = (err) =>
  err?.response?.data?.detail || err?.response?.data?.message || 'Something went wrong. Please try again.';

const ScoreRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
    <div className="flex items-center gap-2 flex-1 max-w-[60%]">
      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
        <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, value || 0)}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-8 text-right">{value ?? '–'}</span>
    </div>
  </div>
);

const Scoreboard = ({ score }) => {
  if (!score) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">No scores yet. Submit a turn to get feedback.</p>;
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-900 dark:text-white">Latest turn score</span>
        <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">{score.overall ?? '–'}</span>
      </div>
      <ScoreRow label="Legal reasoning" value={score.legal_reasoning} />
      <ScoreRow label="Citation accuracy" value={score.citation_accuracy} />
      <ScoreRow label="Procedure" value={score.procedure} />
      <ScoreRow label="Persuasiveness" value={score.persuasiveness} />
      {score.written_feedback && (
        <div className="mt-3 text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/40 rounded-md p-3 border border-gray-200 dark:border-gray-700">
          {score.written_feedback}
        </div>
      )}
      {score.cited_sources_verified === false && (
        <p className="text-xs text-amber-600 dark:text-amber-400">Some cited sources could not be verified.</p>
      )}
    </div>
  );
};

const MockTrialSession = () => {
  const { trialId } = useParams();

  const [trial, setTrial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');

  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [witnessQ, setWitnessQ] = useState('');
  const [examining, setExamining] = useState(false);
  const [concluding, setConcluding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await academyAPI.getTrial(trialId);
      setTrial(res.data || null);
    } catch (err) {
      setNotice(quotaMessage(err));
    } finally {
      setLoading(false);
    }
  }, [trialId]);

  useEffect(() => { load(); }, [load]);

  const latestScore = trial?.scores?.length ? trial.scores[trial.scores.length - 1] : null;

  const isCompleted = trial?.status === 'completed' || trial?.status === 'abandoned';
  const isStudentTurn = trial?.current_actor === 'student' &&
    (trial?.status === 'awaiting_student' || trial?.status === 'in_progress');
  const inputDisabled = submitting || isCompleted || !isStudentTurn;

  const handleSubmitTurn = async (e) => {
    e.preventDefault();
    const c = content.trim();
    if (!c || submitting) return;
    setNotice('');
    setSubmitting(true);
    try {
      await academyAPI.trialTurn(trialId, c);
      setContent('');
      // Re-fetch the full trial so transcript + scores reflect server truth.
      await load();
    } catch (err) {
      setNotice(quotaMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleExamineWitness = async (e) => {
    e.preventDefault();
    const q = witnessQ.trim();
    if (!q || examining) return;
    setNotice('');
    setExamining(true);
    try {
      await academyAPI.examineWitness(trialId, q);
      setWitnessQ('');
      await load();
    } catch (err) {
      setNotice(quotaMessage(err));
    } finally {
      setExamining(false);
    }
  };

  const handleConclude = async () => {
    if (concluding) return;
    setNotice('');
    setConcluding(true);
    try {
      const res = await academyAPI.concludeTrial(trialId);
      setTrial(res.data || trial);
    } catch (err) {
      setNotice(quotaMessage(err));
    } finally {
      setConcluding(false);
    }
  };

  if (loading && !trial) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (!trial) {
    return (
      <div className="container mx-auto px-4 py-10 text-center">
        <p className="text-gray-500 dark:text-gray-400">{notice || 'Trial not found.'}</p>
        <Link to="/student/mock-trials" className="mt-4 inline-block text-primary-600 dark:text-primary-400 hover:underline">
          Back to Mock Trials
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-5xl">
      <Link to="/student/mock-trials" className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4">
        <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to Mock Trials
      </Link>

      <div className="mb-6 flex items-start gap-2">
        <ScaleIcon className="h-6 w-6 text-primary-500 mt-0.5 flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{trial.title || trial.topic_name}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {trial.topic_name} · You are <span className="capitalize font-medium">{trial.student_role}</span>
            {trial.current_phase && <> · Phase: <span className="capitalize">{trial.current_phase}</span></>}
          </p>
        </div>
      </div>

      {notice && (
        <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 p-3 rounded-md text-sm border border-amber-200 dark:border-amber-800">
          {notice}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column: fact pattern + transcript + input */}
        <div className="lg:col-span-2 space-y-6">
          {trial.fact_pattern && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-4 sm:p-5">
              <h2 className="text-base font-medium text-gray-900 dark:text-white mb-2">Fact pattern</h2>
              <div className="text-sm"><MarkdownRenderer content={trial.fact_pattern} /></div>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-4 sm:p-5">
            <h2 className="text-base font-medium text-gray-900 dark:text-white mb-4">Transcript</h2>
            <TrialTranscript turns={trial.turns || []} />
          </div>

          {/* Verdict (after conclude) */}
          {trial.verdict && (
            <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 sm:p-5">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-medium text-gray-900 dark:text-white">Verdict &amp; ruling</h2>
                {typeof trial.final_score === 'number' && (
                  <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">{trial.final_score}</span>
                )}
              </div>
              <div className="text-sm"><MarkdownRenderer content={trial.verdict} /></div>
            </div>
          )}

          {/* Student turn input */}
          {!isCompleted && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-medium text-gray-900 dark:text-white">Your submission</h2>
                {!isStudentTurn && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Waiting for the {trial.current_actor || 'court'}…
                  </span>
                )}
              </div>
              <form onSubmit={handleSubmitTurn}>
                <textarea
                  rows={5}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  disabled={inputDisabled}
                  placeholder={isStudentTurn ? 'Present your argument…' : 'It is not your turn yet.'}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm disabled:opacity-60"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="submit"
                    disabled={inputDisabled || !content.trim()}
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                  >
                    {submitting ? 'Submitting…' : 'Submit turn'}
                  </button>
                  <button
                    type="button"
                    onClick={handleConclude}
                    disabled={concluding}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    {concluding ? 'Concluding…' : 'Conclude trial'}
                  </button>
                </div>
              </form>

              {/* Witness examination */}
              {trial.with_witness && (
                <form onSubmit={handleExamineWitness} className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-700">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Examine the witness</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={witnessQ}
                      onChange={(e) => setWitnessQ(e.target.value)}
                      disabled={examining || isCompleted}
                      placeholder="Put a question to the witness…"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm disabled:opacity-60"
                    />
                    <button
                      type="submit"
                      disabled={examining || isCompleted || !witnessQ.trim()}
                      className="inline-flex items-center px-4 py-2 border border-primary-500 text-primary-600 dark:text-primary-400 rounded-md text-sm font-medium hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50"
                    >
                      {examining ? 'Asking…' : 'Ask'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Sidebar: scoreboard */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-5 lg:sticky lg:top-6">
            <h2 className="text-base font-medium text-gray-900 dark:text-white mb-4">Scoreboard</h2>
            <Scoreboard score={latestScore} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockTrialSession;
